import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Zap, Phone, Building2, DollarSign, Info } from "lucide-react";
import { Link } from "react-router-dom";
import WizardSlider from "./WizardSlider";

interface QuickCalculateStepProps {
  language: string;
  callsPerMonth: number;
  setCallsPerMonth: (value: number) => void;
  numberOfProperties: number;
  setNumberOfProperties: (value: number) => void;
  avgOrderValue: number;
  setAvgOrderValue: (value: number) => void;
  quickEstimate: number;
  paybackMonths: number;
  yearOneROI: number;
  formatCurrency: (value: number) => string;
  onSwitchToFull: () => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const QuickCalculateStep = ({
  language,
  callsPerMonth,
  setCallsPerMonth,
  numberOfProperties,
  setNumberOfProperties,
  avgOrderValue,
  setAvgOrderValue,
  quickEstimate,
  paybackMonths,
  yearOneROI,
  formatCurrency,
  onSwitchToFull
}: QuickCalculateStepProps) => {
  const tr = {
    title: language === 'sv' ? 'Snabb ROI-uppskattning' : 'Quick ROI Estimate',
    subtitle: language === 'sv' ? '3 inputs, direkta resultat' : '3 inputs, instant results',
    callsPerMonth: language === 'sv' ? 'Totala samtal per månad' : 'Total Calls per Month',
    callsHelper: language === 'sv' ? 'Alla fastigheter kombinerat' : 'All properties combined',
    properties: language === 'sv' ? 'Antal fastigheter' : 'Number of Properties',
    avgOrder: language === 'sv' ? 'Snittvärde per vistelse' : 'Avg Stay Value',
    annualSavings: language === 'sv' ? 'Uppskattade årliga besparingar' : 'Estimated Annual Savings',
    payback: language === 'sv' ? 'Återbetalningstid' : 'Payback',
    months: language === 'sv' ? 'mån' : 'mo',
    roi: language === 'sv' ? 'År 1 ROI' : 'Year 1 ROI',
    getDetailed: language === 'sv' ? 'Få detaljerad analys' : 'Get Detailed Analysis',
    bookDemo: language === 'sv' ? 'Boka demo' : 'Book Demo',
    disclaimer: language === 'sv' 
      ? '* Använder branschgenomsnitt: 40% missade samtal, 20% säljsamtal, $25/h personal'
      : '* Using industry averages: 40% missed calls, 20% sales calls, $25/hr staff'
  };

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={itemVariants} className="text-center mb-4">
        <Badge className="mb-3 bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 px-4 py-1.5 text-sm">
          <Zap className="h-4 w-4 mr-1.5" />
          {language === 'sv' ? 'Snabbläge' : 'Quick Mode'}
        </Badge>
        <h3 className="text-2xl sm:text-3xl font-bold mb-2">{tr.title}</h3>
        <p className="text-base text-muted-foreground">{tr.subtitle}</p>
      </motion.div>

      <motion.div variants={itemVariants}>
        <WizardSlider
          label={tr.callsPerMonth}
          helperText={tr.callsHelper}
          value={callsPerMonth}
          onChange={setCallsPerMonth}
          min={50}
          max={2000}
          step={10}
          icon={Phone}
          animated
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <WizardSlider
          label={tr.properties}
          value={numberOfProperties}
          onChange={setNumberOfProperties}
          min={1}
          max={500}
          step={numberOfProperties <= 50 ? 1 : numberOfProperties <= 100 ? 5 : 10}
          icon={Building2}
          animated
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <WizardSlider
          label={tr.avgOrder}
          value={avgOrderValue}
          onChange={setAvgOrderValue}
          min={50}
          max={1000}
          step={10}
          prefix="$"
          icon={DollarSign}
          animated
        />
      </motion.div>

      {/* Instant Results Card */}
      <motion.div variants={itemVariants}>
        <Card className="p-6 sm:p-8 bg-gradient-to-br from-brand-600 via-brand-500 to-pink-500 text-white shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16" />
          <div className="relative z-10 text-center">
            <p className="text-sm text-white/80 mb-2">{tr.annualSavings}</p>
            <motion.p 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.2 }}
              key={quickEstimate}
            >
              {formatCurrency(quickEstimate)}
            </motion.p>
            <div className="flex justify-center gap-6 text-sm">
              <span className="flex items-center gap-1.5">
                <span className="text-white/70">{tr.payback}:</span>
                <span className="font-bold text-lg">{paybackMonths.toFixed(1)} {tr.months}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="text-white/70">{tr.roi}:</span>
                <span className="font-bold text-lg">{yearOneROI.toFixed(0)}%</span>
              </span>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* CTAs */}
      <motion.div variants={itemVariants} className="flex gap-4">
        <Button 
          variant="outline" 
          className="flex-1 h-12 text-base"
          onClick={onSwitchToFull}
        >
          {tr.getDetailed}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <Button className="flex-1 h-12 text-base" asChild>
          <Link to={`/${language}/demo`}>
            {tr.bookDemo}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </Button>
      </motion.div>

      <motion.p variants={itemVariants} className="text-sm text-center text-muted-foreground flex items-center justify-center gap-1.5">
        <Info className="h-4 w-4" />
        {tr.disclaimer}
      </motion.p>
    </motion.div>
  );
};

export default QuickCalculateStep;
